import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { ValidatorForm } from 'react-form-validator-core';
import flatten from 'lodash/flatten';

import useAlerts from 'core/hooks/useAlerts';
import useErrorHandlers from 'core/hooks/useErrorHandlers';

import {
  Box,
  Button,
  Container,
  H4,
  Hr,
  Header,
  Flex,
  FormGroup,
  Label,
  RouterLink,
  Text,
  ValidatedInput,
} from 'core/ui/atoms';
import { SEND_RESET_PASSWORD_EMAIL } from 'core/api/apollo/mutations';
import { ERROR_MESSAGES } from 'core/api/constants';

function ForgotPasswordPage() {
  const [isSent, setIsSent] = useState(false);

  return (
    <Container>
      <Flex alignItems='center' justifyContent='center'>
        <FullWidthContainer>
          <WelcomeHeader>Login</WelcomeHeader>
          <Hr />
          {isSent
            ? (
              <EmailSent>
                <H4>Email Sent!</H4>
                <Text>Please follow the instructions in the email</Text>
              </EmailSent>
            ) : (
              <ForgotPasswordForm
                onSuccess={() => setIsSent(true)}
              />
            )}
        </FullWidthContainer>
      </Flex>
    </Container>
  );
}

function ForgotPasswordForm(props) {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const alerts = useAlerts();
  const [sendResetPasswordEmail] = useMutation(SEND_RESET_PASSWORD_EMAIL, {
    variables: { input: { email } },
  });
  const { displayApolloResponseErrors } = useErrorHandlers();

  const onChange = (e) => setEmail(e.target.value);

  const onError = (errors) => {
    setIsProcessing(false);
    displayApolloResponseErrors(flatten([errors]));
  };

  const onComplete = ({ data: { sendResetPasswordEmail: result } }) => {
    setIsProcessing(false);

    if (result.errors && result.errors.length) {
      return onError(result.errors);
    }

    props.onSuccess && props.onSuccess();
  };

  const onSubmit = (e) => {
    e.preventDefault();

    alerts.clear();
    setIsProcessing(true);
    sendResetPasswordEmail().then(onComplete).catch(onError);
  };

  return (
    <Form
      onSubmit={onSubmit}
      noValidate
      instantValidate={false}
    >
      <EmailFormGroup>
        <InputLabel required>Email</InputLabel>
        <ValidatedInputText
          type="email"
          name="email"
          onChange={onChange}
          value={email}
          validators={['required', 'isEmail']}
          errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.email]}
        />
      </EmailFormGroup>
      <ButtonCont>
        <FormGroup>
          <SubmitButton type="submit" btn-primary btn-lg disabled={isProcessing}>
            {isProcessing ? 'Sending...' : 'Reset My Password'}
          </SubmitButton>
        </FormGroup>
      </ButtonCont>
      <LoginLink to="/sign-in">Back to Login</LoginLink>
    </Form>
  );
}

ForgotPasswordForm.propTypes = {
  onSuccess: PropTypes.func,
};

const FullWidthContainer = styled(Box)`
  width: 100%;
`;

const LoginLink = styled(RouterLink)`
  margin-top: 20px;
  font-size: 12px;
  padding: 0 calc(2vw);
  text-align: center;
  font-weight: 400;
  font-size: 16px;
  text-decoration: underline;
  text-decoration-style: solid;
  display: flex;
  justify-content: center;
`;

const SubmitButton = styled(Button)`
  border-radius: 4px;
  padding: 12px 24px;
  height: 52px;
  border-color: ${(p) => p.theme.colors.green.light};
  background: ${(p) => p.theme.colors.green.light};
  color: ${(p) => p.theme.colors.primary};
  text-transform: uppercase;
  ${(p) => p.theme.max('md')`
    width: 90% !important;
    color: ${(p) => p.theme.colors.primary};
    text-transform: uppercase;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 10%;
  `}
`;

const ValidatedInputText = styled(ValidatedInput)`
  border-radius: 4px;
  border: solid 1px #36443A;
  padding: 0.65em 1em;
`;

const InputLabel = styled(Label)`
  padding: 0.5em 0 0.5em 0em;
`;

const WelcomeHeader = styled(Header)`
  margin-top: 1vh;
`;

const EmailFormGroup = styled(FormGroup)`
  margin-bottom: 2vh !important;
`;

const Form = styled(ValidatorForm)`
  display: block;
  width: 90%;
  margin: 7vh auto 3vh auto;
  > div:nth-child(1) {
    margin-bottom: 5vh;
  }
  ${(p) => p.theme.min('sm')`
    width: 320px;
  `}
  ${(p) => p.theme.max('sm')`
    padding: 0 calc(${(p) => p.theme.layout.mobilePadding} * 2);
  `}
`;

const ButtonCont = styled.div`
  text-align: center;
  margin-top: 5vh;
  ${(p) => p.theme.max('sm')`
    button {
      width: 100%;
    }
  `}
`;

const EmailSent = styled.div`
  text-align: center;
  margin: 20px 0;
  padding: 30px;
`;

export default ForgotPasswordPage;
